import { AppAction } from 'actions/types/ActionTypes'
import { API_CALL } from 'actions/actionConstants'
import createBatchedPromise from 'api/createBatchedPromise'
import { BUSINESS_TRAVELLER_API_ACTIONS } from 'reducers/businessTravellerApiActionReducers'
import requestPostItemPolicyEvaluate, {
  type ItemPolicyEvaluateItemInputWire,
} from 'businessTraveller/api/requestPostItemPolicyEvaluate'
import { mapItemPolicyEvaluationResult } from 'businessTraveller/api/mappers/itemPolicyMappers'

type BatchArg = [
  cacheKey: string,
  item: App.BusinessTraveller.ItemPolicyEvaluationItem,
  employeeIds: Array<string>,
  tripRequestId?: string,
]

type ItemPolicyEvaluationsAccumulatedArgs = {
  items: Record<string, ItemPolicyEvaluateItemInputWire>
}

type ItemPolicyEvaluationsBatchResponse = Record<string, App.BusinessTraveller.ItemPolicyEvaluationResult>

// Both the budget bar (cost-less items) and the offer credit badge (cost items) dispatch this one
// action. Items are accumulated by key and sent in a single POST /item-policies/evaluate per tick.
const requestItemPolicyEvaluations = createBatchedPromise<
  ItemPolicyEvaluationsBatchResponse,
  App.BusinessTraveller.ItemPolicyEvaluationResult,
  BatchArg,
  ItemPolicyEvaluationsAccumulatedArgs,
  string
>({
  argsReducerFn: (accumulatedArgs, singleArg) => {
    const [, item] = singleArg
    return {
      items: {
        ...(accumulatedArgs.items ?? {}),
        [item.key]: item as ItemPolicyEvaluateItemInputWire,
      },
    }
  },
  batchRequestFn: async(batchedArgs, params: string) => {
    // params is "${businessId}|${employeeIds.sort().join(',')}|${tripRequestId ?? ''}"
    const [businessId, employeeIdsKey, tripRequestId] = params.split('|')
    const employeeIds = employeeIdsKey ? employeeIdsKey.split(',') : []
    const result = await requestPostItemPolicyEvaluate(businessId, {
      employeeIds,
      items: Object.values(batchedArgs.items),
      ...(tripRequestId ? { tripRequestId } : {}),
    })
    return Object.fromEntries(
      Object.entries(result).map(([key, itemResult]) => [key, mapItemPolicyEvaluationResult(itemResult)]),
    )
  },
  singleValueFn: (singleArg, batchedResponse) => {
    const [, item] = singleArg
    const result = batchedResponse[item.key]
    if (!result) {
      throw new Error(`No evaluation result returned for item key: ${item.key}`)
    }
    return result
  },
})

interface Params {
  refetch?: boolean
}

function fetchItemPolicyEvaluations(batchArg: BatchArg, businessId: string, params?: Params): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const [cacheKey, , employeeIds, tripRequestId] = batchArg
    const current = state.businessTraveller.itemPolicyEvaluations?.[cacheKey]

    if (!current || current.status === 'failed' || params?.refetch) {
      const employeeIdsKey = [...employeeIds].sort().join(',')
      dispatch({
        type: API_CALL,
        api: BUSINESS_TRAVELLER_API_ACTIONS.FETCH_ITEM_POLICY_EVALUATIONS,
        cacheKey,
        request: () => requestItemPolicyEvaluations(batchArg, `${businessId}|${employeeIdsKey}|${tripRequestId ?? ''}`),
      })
    }
  }
}

export default fetchItemPolicyEvaluations
